import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  error: Error;
  resetError: () => void;
}

/**
 * DefaultErrorFallback - UI affichée quand une erreur est capturée
 *
 * Composant affiché par ErrorBoundary quand une erreur survient.
 * Affiche un message convivial et propose des actions de récupération.
 */
const DefaultErrorFallback: React.FC<Props> = ({ error, resetError }) => {
  const { t } = useTranslation();

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // En développement, afficher les détails de l'erreur
  const isDevelopment = import.meta.env.DEV;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        {/* Icône d'erreur */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* Titre */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-4">
          {t('errors.somethingWentWrong')}
        </h1>

        {/* Message principal */}
        <p className="text-gray-600 text-center mb-8 whitespace-pre-line">
          {t('errors.somethingWentWrongDescription')}
        </p>

        {/* Détails de l'erreur (dev uniquement) */}
        {isDevelopment && (
          <div className="mb-8 p-4 bg-gray-100 rounded-lg border border-gray-300">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              {t('errors.errorDetails')}
            </h3>
            <p className="text-xs text-red-600 font-mono mb-2">{error.message}</p>
            {error.stack && (
              <pre className="text-xs text-gray-600 overflow-auto max-h-40 font-mono">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={resetError}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            {t('errors.tryAgain')}
          </button>

          <button
            onClick={handleRefresh}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            {t('errors.reloadPage')}
          </button>

          <button
            onClick={handleGoHome}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Home className="w-5 h-5" />
            {t('errors.goHome')}
          </button>
        </div>

        {/* Message de support */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            {t('errors.contactSupportMessage')}{' '}
            <a href="mailto:support@facturxpro.com" className="text-blue-600 hover:underline">
              support@facturxpro.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DefaultErrorFallback;
